import React, { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import SharedFaq from "@/components/SharedFaq";
import { Card } from "@/components/ui/card";
import { ChevronDown, ArrowRight } from "lucide-react";
import { faqCategories, faqItems } from "@/data/faq";

export default function FAQ() {
  const [active, setActive] = useState(faqCategories[0]);
  const [openIdx, setOpenIdx] = useState(null);

  const items = faqItems.filter((f) => f.cat === active);

  // JSON-LD FAQPage
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="grain-bg">
      <Seo
        title="Häufig gestellte Fragen (FAQ)"
        description="Antworten auf die häufigsten Fragen rund um Laktoseintoleranz, Probiotika, Darm und Regeneration - plus gemeinsame FAQ mit FoodBone."
        keywords="Lactoseintoleranz FAQ, Probiotika, Darm Regeneration, Milch Toleranzaufbau"
        path="/faq"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-6 lg:pt-24">
        <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">FAQ</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
          Häufig gestellte Fragen
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-3xl leading-relaxed">
          Antworten auf die wichtigsten Fragen rund um Laktoseintoleranz, Probiotika, Darm-Regeneration und den LacLive-Ansatz. Grundlegende Fragen zu Milch, Laktose und Knochen findest du weiter unten in der gemeinsamen FAQ mit FoodBone.
        </p>
      </section>

      {/* Autoren-FAQ */}
      <section className="max-w-4xl mx-auto px-6 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold mb-3">Aus der Praxis</div>
          <h2 className="text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">Fragen aus der LacLive-Erfahrung</h2>
        </div>

        {/* Kategorie-Filter */}
        <div className="flex flex-wrap gap-2 mb-6" data-testid="faq-categories">
          {faqCategories.map((c) => (
            <button
              key={c}
              onClick={() => { setActive(c); setOpenIdx(null); }}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                active === c
                  ? "bg-emerald-600 border-emerald-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-emerald-300 hover:text-emerald-700"
              }`}
              data-testid={`faq-cat-${c.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "und")}`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Akkordeon */}
        <div className="space-y-3">
          {items.map((item, i) => {
            const open = openIdx === i;
            return (
              <Card key={i} className="rounded-2xl border-slate-100 overflow-hidden">
                <button
                  onClick={() => setOpenIdx(open ? null : i)}
                  className="w-full text-left px-5 py-4 flex items-start justify-between gap-4 hover:bg-slate-50 transition-colors"
                  data-testid={`faq-q-${i}`}
                >
                  <span className="font-semibold text-slate-900 leading-snug">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`} />
                </button>
                {open && (
                  <div className="px-5 pb-5 text-slate-700 leading-relaxed" data-testid={`faq-a-${i}`}>
                    {item.a}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* CTA nach Autoren-FAQ */}
        <div className="mt-10 rounded-2xl bg-emerald-50 border border-emerald-100 p-6 flex flex-wrap gap-4 items-center justify-between">
          <div>
            <div className="font-semibold text-slate-900">Noch tiefer einsteigen?</div>
            <p className="text-sm text-slate-600 mt-1">Der 12-Wochen-Plan setzt diese Antworten in ein konkretes Protokoll um.</p>
          </div>
          <Link to="/paywall" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:text-emerald-800" data-testid="faq-cta-paywall">
            Zum Ernährungsplan <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-4xl mx-auto px-6">
        <div className="h-px bg-slate-200" />
      </div>

      {/* Shared FAQ (FoodBone) */}
      <SharedFaq />
    </div>
  );
}
